import EditButton from './EditButton'
import {FC, useEffect, useState} from 'react'
import {toAbsoluteUrl} from '_metronic/helpers'
import Card from './Card'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Input from 'app/components/Input'
import Textarea from 'app/components/Textarea'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {addDoc, collection, doc, onSnapshot, serverTimestamp, setDoc} from '@firebase/firestore'
import {ToastContainer} from 'react-toastify'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'
import {v4 as uuid} from 'uuid'
import CustomDrawer from './Drawer'

export type HeaderDataType = {
  title: string
  description: string
  masterServiceAgreement: string
  privacyPolicy: string
  links: Array<{id: string; title: string; url: string}>
}

type Props = {
  headerDetails: HeaderDataType
}

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Title is required'),
  description: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(800, 'Maximum 800 characters')
    .required('Description is required'),
  masterServiceAgreement: Yup.string()
    .url('Must be a valid URL')
    .min(10, 'Minimum 10 characters')
    .max(200, 'Maximum 200 characters')
    .required('Master Service Agreement is required'),
  privacyPolicy: Yup.string()
    .url('Must be a valid URL')
    .min(10, 'Minimum 10 characters')
    .max(200, 'Maximum 200 characters')
    .required('Privacy Policy is required'),
  links: Yup.array()
    .of(
      Yup.object({
        id: Yup.string(),
        title: Yup.string().required('Title is required'),
        url: Yup.string().url('Must be a valid URL').required('URL is required'),
      })
    )
    .optional(),
})

const Header: FC<Props> = ({headerDetails}) => {
  const {currentUser} = useAuth()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [loading, setLoading] = useState(false)

  const [companyDetails, setCompanyDetails] = useState({name: '', logoURL: ''})

  const generateUniqueId = () => {
    return uuid()
  }

  const urlSchema = Yup.string().url('Must be valid url')

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const companyDetailsRef = doc(db, 'tenants', tenantId, 'settings', 'company')

    const unsubscribe = onSnapshot(companyDetailsRef, (snapshot) => {
      if (snapshot.exists()) {
        setCompanyDetails({
          name: snapshot.data().displayName,
          logoURL: snapshot.data().logoUrl,
        })
      }
    })

    return () => unsubscribe()
  }, [])

  const formik = useFormik<HeaderDataType>({
    initialValues: {
      title: '',
      description: '',
      masterServiceAgreement: '',
      privacyPolicy: '',
      links: [],
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const detailsRef = doc(db, 'tenants', tenantId, 'trust_center', 'headerDetails')

        await setDoc(detailsRef, {
          title: formik.values.title.trim(),
          description: formik.values.description.trim(),
          masterServiceAgreement: formik.values.masterServiceAgreement.trim(),
          privacyPolicy: formik.values.privacyPolicy.trim(),
          links: [...formik.values.links],
          companyName: companyDetails.name,
          companyLogoURL: companyDetails.logoURL,
        })

        const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
        const currentDate = new Date()
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth() + 1
        const day = currentDate.getDate()
        const todaysDate = month + '/' + day + '/' + year

        await addDoc(activitiesRef, {
          message: 'Header details for trust center has been updated',
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          username: currentUser?.userName,
          date: todaysDate,
          email: currentUser?.email,
          target: 'Trust Center',
          action: 'Header details updated',
        })

        showSuccessToast({message: 'Header has been updated successfully'})
      } catch (error) {
        console.log('Error while updating header for security profile', error)
      } finally {
        setLoading(false)
        setIsDrawerOpen(false)
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
    },
  })

  useEffect(() => {
    formik.setValues(headerDetails)
  }, [isDrawerOpen])

  const handleRemoveLink = (linkId: string) => {
    const currentLinks = formik.values.links || []
    formik.setFieldValue(
      'links',
      currentLinks.filter((link) => linkId !== link.id)
    )
  }

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
    formik.resetForm()
  }

  return (
    <div className='h-100'>
      <CustomDrawer
        isDrawerOpen={isDrawerOpen}
        onCancel={onCancel}
        title={'Header'}
        loading={loading}
        submitHandler={() => formik.submitForm()}
      >
        <div
          style={{
            padding: '20px 20px 150px',
          }}
        >
          <div className='form' onSubmit={() => formik.submitForm()}>
            <div className='form-group mb-5'>
              <Input
                type='text'
                label='Title'
                placeholder='Trust Center Title'
                error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
                {...formik.getFieldProps('title')}
              />
            </div>

            <div className='form-group mb-5'>
              <Textarea
                label='Description'
                placeholder='Type Trust Center Description...'
                error={
                  formik.touched.description && formik.errors.description
                    ? formik.errors.description
                    : ''
                }
                {...formik.getFieldProps('description')}
              />
            </div>

            <div className='d-flex flex-column mb-5'>
              <div className='pb-1 mb-5 border-bottom border-gray-300'>
                <span className='fw-medium text-dark fs-6'>Essential Links</span>
              </div>

              <div className='flex-fill d-flex gap-3 mb-3'>
                <Input
                  type='text'
                  value={'Master Service Agreement'}
                  readOnly
                  onFocus={(e) => {
                    e.target.style.outline = 'none'
                  }}
                />
                <Input
                  type='url'
                  placeholder='Enter URL'
                  error={
                    formik.touched.masterServiceAgreement && formik.errors.masterServiceAgreement
                      ? formik.errors.masterServiceAgreement
                      : ''
                  }
                  {...formik.getFieldProps('masterServiceAgreement')}
                />
              </div>

              <div className='flex-fill d-flex gap-3'>
                <Input
                  type='text'
                  value={'Privacy Policy'}
                  readOnly
                  onFocus={(e) => {
                    e.target.style.outline = 'none'
                  }}
                />
                <Input
                  type='url'
                  placeholder='Enter URL'
                  error={
                    formik.touched.privacyPolicy && formik.errors.privacyPolicy
                      ? formik.errors.privacyPolicy
                      : ''
                  }
                  {...formik.getFieldProps('privacyPolicy')}
                />
              </div>
            </div>

            <div className='d-flex flex-column'>
              <div className='d-flex justify-content-between align-items-center pb-1 mb-5 border-bottom border-gray-300'>
                <span className='fw-medium text-dark fs-6'>Additional Links</span>

                <Button
                  btnType='transparent'
                  onClick={() => {
                    if (formik.values.links.length === 0) {
                      formik.setFieldValue('links', [{id: generateUniqueId(), title: '', url: ''}])
                    } else {
                      const title = formik.values.links[formik.values.links.length - 1]?.title
                      const url = formik.values.links[formik.values.links.length - 1]?.url

                      if (title?.length > 0 && url?.length > 0 && urlSchema.isValidSync(url)) {
                        formik.setFieldValue('links', [
                          ...formik.values.links,
                          {id: generateUniqueId(), title: '', url: ''},
                        ])
                      }
                    }
                  }}
                >
                  <img
                    alt='plus-icon'
                    src={toAbsoluteUrl('/media/icon/plus.svg')}
                    className='me-2'
                    style={{height: '18px', width: '18px'}}
                  />
                  <span>Add Link</span>
                </Button>
              </div>

              {formik.values.links.length > 0 ? (
                formik.values.links.map((link, index) => (
                  <div key={link.id} className='d-flex gap-3 mb-3'>
                    <Input
                      type='text'
                      placeholder='Enter Title'
                      error={
                        formik.touched.links?.[index]?.title &&
                        formik.values.links?.[index]?.title.trim().length === 0
                          ? 'Title is required'
                          : ''
                      }
                      {...formik.getFieldProps(`links[${index}].title`)}
                    />

                    <Input
                      type='url'
                      placeholder='Enter URL'
                      error={
                        formik.touched.links?.[index]?.url
                          ? formik.values.links?.[index]?.url.trim().length === 0
                            ? 'URL is required'
                            : urlSchema.isValidSync(formik.values.links?.[index].url)
                            ? ''
                            : 'Must be valid URL'
                          : ''
                      }
                      {...formik.getFieldProps(`links[${index}].url`)}
                    />

                    <Button
                      style={{
                        width: '43.5px',
                        height: '43.5px',
                      }}
                      onClick={() => {
                        handleRemoveLink(link.id)
                      }}
                    >
                      <img
                        alt='trash-icon'
                        src={toAbsoluteUrl('/media/icon/trash.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>
                  </div>
                ))
              ) : (
                <div
                  className='w-100 d-flex justify-content-center align-items-center'
                  style={{height: '245px'}}
                >
                  <div className='d-flex flex-column align-items-center gap-5'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        backgroundColor: '#F3F4F6',
                        borderRadius: '12px',
                        height: '48px',
                        width: '48px',
                      }}
                    >
                      <img
                        alt='link-icon'
                        src={toAbsoluteUrl('/media/icon/link.svg')}
                        className=''
                        style={{height: '32px', width: '32px'}}
                      />
                    </div>

                    <span className='text-dark fw-medium fs-6'>No links added</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </CustomDrawer>

      <Card
        title='Header'
        actionComponent={
          <EditButton
            onClickHandler={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          />
        }
      >
        <div className='d-flex flex-column gap-5'>
          <div className='d-flex flex-column gap-2'>
            <h3 className='text-dark fw-medium fs-4'>{headerDetails.title}</h3>

            <p className='text-secondary-gray fs-6 mb-0'>{headerDetails.description}</p>
          </div>

          <div>
            <span className='text-dark fw-medium fs-6'>Links</span>

            <div className='d-flex flex-wrap gap-3 mt-2'>
              {headerDetails?.masterServiceAgreement &&
                headerDetails?.masterServiceAgreement.trim() !== '' && (
                  <a
                    href={headerDetails.masterServiceAgreement}
                    target='_blank'
                    className='d-flex align-items-center gap-1'
                  >
                    <img
                      alt='link-icon'
                      src={toAbsoluteUrl('/media/icon/link.svg')}
                      className=''
                      style={{height: '18px', width: '18px'}}
                    />

                    <span className='text-dark fs-6' style={{textDecoration: 'underline'}}>
                      Master Service Agreement
                    </span>
                  </a>
                )}

              {headerDetails?.privacyPolicy && headerDetails?.privacyPolicy.trim() !== '' && (
                <a href={headerDetails.privacyPolicy} className='d-flex align-items-center gap-1'>
                  <img
                    alt='link-icon'
                    src={toAbsoluteUrl('/media/icon/link.svg')}
                    className=''
                    style={{height: '18px', width: '18px'}}
                  />

                  <span className='text-dark fs-6' style={{textDecoration: 'underline'}}>
                    Privacy Policy
                  </span>
                </a>
              )}

              {headerDetails.links.map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  target='_blank'
                  className='d-flex align-items-center gap-1'
                >
                  <img
                    alt='link-icon'
                    src={toAbsoluteUrl('/media/icon/link.svg')}
                    className=''
                    style={{height: '18px', width: '18px'}}
                  />

                  <span className='text-dark fs-6' style={{textDecoration: 'underline'}}>
                    {link.title}
                  </span>
                </a>
              ))}

              {headerDetails?.masterServiceAgreement?.trim() === '' &&
                headerDetails?.privacyPolicy?.trim() === '' && (
                  <span className='text-secondary-gray fs-7'>No links available at this time</span>
                )}
            </div>
          </div>
        </div>
      </Card>

      <ToastContainer />
    </div>
  )
}

export default Header
